import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { SubjectModal } from '@/components/SubjectModal';
import { DeleteConfirmModal } from '@/components/DeleteConfirmModal';
import { DraggableList } from '@/components/DraggableList';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Trash2, Edit, ChevronRight, Loader2 } from 'lucide-react';
import { subjectsApi, topicsApi } from '@/services/api';

export default function Subjects() {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [topicCounts, setTopicCounts] = useState({});

  useEffect(() => {
    loadSubjects();
  }, []);

  const loadSubjects = async () => {
    try {
      setLoading(true);
      const res = await subjectsApi.getAll();
      setSubjects(res.data || []);
    } catch (error) {
      toast.error('Dersler yüklenemedi');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSubjects = useMemo(() => {
    if (!search) return subjects;
    return subjects.filter(s => 
      s.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [subjects, search]);

  const handleSave = async (data) => {
    try {
      if (editingSubject) {
        await subjectsApi.update(editingSubject._id, data);
        toast.success('Ders güncellendi');
      } else {
        await subjectsApi.create(data);
        toast.success('Ders oluşturuldu');
      }
      setModalOpen(false);
      setEditingSubject(null);
      loadSubjects();
    } catch (error) {
      toast.error('Bir hata oluştu');
      console.error(error);
    }
  };

  const handleDelete = async () => {
    try {
      await Promise.all(selected.map(id => subjectsApi.delete(id)));
      toast.success(`${selected.length} ders silindi`);
      setSelected([]);
      setDeleteModalOpen(false);
      loadSubjects();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
      console.error(error);
    }
  };

  const handleReorder = async (newItems) => {
    setSubjects(newItems);
    try {
      await Promise.all(
        newItems.map((item, index) => 
          subjectsApi.update(item._id, { order: index })
        )
      );
      toast.success('Sıralama güncellendi');
    } catch (error) {
      toast.error('Sıralama kaydedilemedi');
      loadSubjects();
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selected.length === filteredSubjects.length) {
      setSelected([]);
    } else {
      setSelected(filteredSubjects.map(s => s._id));
    }
  };


  useEffect(() => {
  const loadData = async () => {
    try {

      const questionCountMap = {};

      await Promise.all(
        subjects.map(async (subject) => {
          const res = await topicsApi.getBySubject(subject._id);

          const topics = res.data || [];

         

          // 🔥 toplam sual sayını hesabla
          const totalQuestions = topics.reduce(
            (sum, t) => sum + (t.questionCount || 0),
            0
          );

          questionCountMap[subject._id] = totalQuestions;
        })
      );

      setTopicCounts(questionCountMap);
    

    } catch (err) {
      console.error(err);
    }
  };

  if (subjects.length > 0) {
    loadData();
  }
}, [subjects]);

  const openEdit = (subject) => {
    setEditingSubject(subject);
    setModalOpen(true);
  };

  const renderSubjectItem = (subject) => (
    <Card 
      className="hover:border-teal-200 transition-colors"
      data-testid={`subject-item-${subject._id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <Checkbox
            checked={selected.includes(subject._id)}
            onCheckedChange={() => toggleSelect(subject._id)}
            data-testid={`select-subject-${subject._id}`}
          />
                  <p className="text-xs text-slate-500 mt-1">
  {topicCounts[subject._id] ?? 0} sual
</p>
          <span className="text-3xl">{subject.icon || '📚'}</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 truncate">{subject.name}</h3>
    
            <div className="flex gap-1 mt-1 flex-wrap">
              {subject.groups?.map(g => (
                <Badge key={g} variant="secondary" className="text-xs">
                  {g}. Sınıf
                </Badge>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openEdit(subject)}
              data-testid={`edit-subject-${subject._id}`}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Link to={`/subjects/${subject._id}/topics`}>
              <Button variant="ghost" size="icon" data-testid={`view-topics-${subject._id}`}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout breadcrumbs={[{ label: 'Dersler' }]}>
      <div className="space-y-6" data-testid="subjects-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Dersler
            </h1>
            <p className="text-slate-500">Tüm derslerinizi yönetin</p>
          </div>
          <Button 
            onClick={() => { setEditingSubject(null); setModalOpen(true); }}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="add-subject-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Yeni Ders
          </Button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Ders ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-subjects"
            />
          </div>
          {selected.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-500">{selected.length} seçili</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteModalOpen(true)}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Sil
              </Button>
            </div>
          )}
        </div>

        {/* Select All */}
        {filteredSubjects.length > 0 && (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={selected.length === filteredSubjects.length && filteredSubjects.length > 0}
              onCheckedChange={toggleSelectAll}
              data-testid="select-all-subjects"
            />
            <span className="text-sm text-slate-500">Tümünü seç</span>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : filteredSubjects.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-lg font-semibold text-slate-900">Henüz ders yok</h3>
            <p className="text-slate-500 mt-1">Yeni bir ders ekleyerek başlayın</p>
            <Button 
              onClick={() => setModalOpen(true)}
              className="mt-4 bg-teal-700 hover:bg-teal-800"
            >
              <Plus className="w-4 h-4 mr-2" /> İlk Dersi Ekle
            </Button>
          </Card>
        ) : (
          <DraggableList
            items={filteredSubjects}
            onReorder={handleReorder}
            renderItem={renderSubjectItem}
            disabled={search.length > 0}
          />
        )}
      </div>

      <SubjectModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingSubject(null); }}
        onSave={handleSave}
        subject={editingSubject}
      />

      <DeleteConfirmModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        count={selected.length}
      />
    </Layout>
  );
}
